import { ClientError } from './errors/ClientError';
import { ServerError } from './errors/ServerError';
import {
  ValidationError,
  ValidationFieldError,
} from './errors/ValidationError';
import { WEB_API_URL } from 'config/api';
import ForbiddenError from './errors/ForbiddenError';
import queryString from 'query-string';
import UnauthorizedError from './errors/UnauthorizedError';

export type FetchWebApi = <R = any, P = any>(
  path: string,
  queryParams?: P,
  options?: RequestInit,
) => Promise<R>;

const fetchWebApi: FetchWebApi = async (
  path,
  queryParams,
  requestOptions = { headers: new Headers() },
) => {
  const defaultHeaders = new Headers();
  const headers =
    requestOptions.headers instanceof Headers
      ? requestOptions.headers
      : new Headers(requestOptions.headers);

  if (!(requestOptions.body instanceof FormData))
    defaultHeaders.append('Content-Type', 'application/json');

  const token = sessionStorage.getItem('token');

  if (token)
    defaultHeaders.append('Authorization', `Basic ${JSON.parse(token)}`);

  const options: RequestInit = {
    ...requestOptions,
    headers: mergeHeaders(defaultHeaders, headers),
  };

  const response = await fetch(
    `${WEB_API_URL}${path}${
      queryParams ? '?' + queryString.stringify(queryParams) : ''
    }`,
    options,
  );

  if ((response.status === 401 || response.status === 403) && window.Cypress) {
    return {} as any;
  }

  if (response.status === 401) throw new UnauthorizedError();

  if (response.status === 403) throw new ForbiddenError();

  if (response.status === 400) {
    const { errors }: { errors: ValidationFieldError[] } =
      await response.json();
    throw new ValidationError(errors, response.status);
  }

  if (response.status > 401 && response.status < 500)
    throw new ClientError(response.status);

  if (response.status > 499) throw new ServerError(response.status);

  try {
    if (response.headers.get('Content-Type') === 'text/html')
      return response.text();
    else if (response.headers.get('Content-Type') === 'application/pdf')
      return response.blob();
    else if (response.status !== 204) return response.json();
  } catch {
    return;
  }
};

const mergeHeaders = (defaultHeaders: Headers, headers: Headers): Headers => {
  const mergedHeaders = new Headers();

  defaultHeaders.forEach((value, key) => {
    mergedHeaders.set(key, value);
  });

  headers.forEach((value, key) => {
    mergedHeaders.set(key, value);
  });

  return mergedHeaders;
};

export default fetchWebApi;
